import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { AlertTriangle, Pencil, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { eur, pct, margeText, margeBadge, STATUT_CHANTIER } from "../../lib/format";
import { useViewRole } from "../../context/ViewRoleContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Progress } from "../ui/progress";

const FILTERS = ["Tous", "En cours", "Clôturé", "À risque"];

export default function ChantiersView() {
  const { canSeeMargins, isAdmin } = useViewRole();
  const [chantiers, setChantiers] = useState([]);
  const [filter, setFilter] = useState("Tous");
  const [edit, setEdit] = useState(null);

  const load = () => api.get("/chantiers").then((r) => setChantiers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = chantiers.filter((c) => {
    if (filter === "Tous") return true;
    if (filter === "À risque") return c.alerte_rentabilite;
    return c.statut === filter;
  });

  const saveEdit = async () => {
    try {
      await api.put(`/chantiers/${edit.id}`, {
        code: edit.code, titre: edit.titre, client_nom: edit.client_nom, commercial: edit.commercial,
        statut: edit.statut, date_debut: edit.date_debut, date_fin_prevue: edit.date_fin_prevue,
        budget_ht: Number(edit.budget_ht), cout_prevu_ht: Number(edit.cout_prevu_ht),
        cout_reel_ht: Number(edit.cout_reel_ht), ca_facture_ht: Number(edit.ca_facture_ht),
        ca_encaisse_ht: Number(edit.ca_encaisse_ht), heures_prevues: Number(edit.heures_prevues),
        heures_passees: Number(edit.heures_passees), progression_pct: Number(edit.progression_pct),
        repartition: edit.repartition || {},
      });
      toast.success("Chantier mis à jour — rentabilité recalculée");
      setEdit(null);
      load();
    } catch (e) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  return (
    <div className="space-y-6" data-testid="chantiers-view">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Rentabilité Chantiers</h1>
          <p className="text-slate-400 text-sm mt-1">Coûts prévus vs réels, marge brute et CA encaissé.</p>
        </div>
        <div className="flex gap-1 p-1 rounded-lg bg-[#111827] border border-[#1E293B]">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`chantier-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                filter === f ? "bg-amber-500 text-[#0B0F17]" : "text-slate-400 hover:text-slate-200"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {filtered.map((c, i) => {
          const conso = Math.min(100, ((c.cout_reel_ht || 0) / (c.budget_ht || 1)) * 100);
          return (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className="rounded-xl bg-[#111827] border border-[#1E293B] p-5"
              data-testid={`chantier-card-${c.code}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono-metric text-xs text-slate-500">{c.code}</span>
                    {c.alerte_rentabilite && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20">
                        <AlertTriangle className="h-3 w-3" /> À risque
                      </span>
                    )}
                  </div>
                  <h3 className="font-head text-lg font-bold mt-0.5">{c.titre}</h3>
                  <p className="text-sm text-slate-400">{c.client_nom} · {c.commercial}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${STATUT_CHANTIER[c.statut] || ""}`}>
                    {c.statut}
                  </span>
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid={`chantier-edit-${c.code}`}
                      onClick={() => setEdit({ ...c })}
                      className="h-7 text-xs text-slate-400 hover:text-amber-400"
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Dépenses
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 mt-4">
                <div className="rounded-lg bg-[#0B0F17] border border-[#1E293B] p-3">
                  <div className="text-[11px] text-slate-500">Budget HT</div>
                  <div className="font-mono-metric text-sm font-semibold text-slate-100">{eur(c.budget_ht)}</div>
                </div>
                <div className="rounded-lg bg-[#0B0F17] border border-[#1E293B] p-3">
                  <div className="text-[11px] text-slate-500">CA facturé</div>
                  <div className="font-mono-metric text-sm font-semibold text-sky-400">{eur(c.ca_facture_ht)}</div>
                </div>
                <div className="rounded-lg bg-[#0B0F17] border border-[#1E293B] p-3">
                  <div className="text-[11px] text-slate-500">Marge réelle</div>
                  <div className={`font-mono-metric text-sm font-semibold ${canSeeMargins ? margeText(c.taux_marge_reel_pct) : "text-slate-600"}`}>
                    {canSeeMargins ? eur(c.marge_reelle_ht) : "•••••"}
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="text-slate-500">Consommation budget</span>
                  <span className={`font-mono-metric font-semibold ${conso > 80 ? "text-red-400" : "text-slate-300"}`}>
                    {canSeeMargins ? pct(conso) : "•••"}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-[#0B0F17] overflow-hidden">
                  <div
                    className={`h-full rounded-full ${conso > 80 ? "bg-red-500" : conso > 60 ? "bg-amber-500" : "bg-emerald-500"}`}
                    style={{ width: `${canSeeMargins ? conso : 0}%` }}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#1E293B]">
                <div className="flex items-center gap-4 text-xs text-slate-400">
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" /> {c.heures_passees}/{c.heures_prevues} h
                  </span>
                  <span>Avancement {c.progression_pct}%</span>
                </div>
                {canSeeMargins && (
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${margeBadge(c.taux_marge_reel_pct)}`}>
                    Taux {pct(c.taux_marge_reel_pct)}
                  </span>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-[#111827] border-[#1E293B] text-slate-100">
          <DialogHeader>
            <DialogTitle className="font-head">Ajuster les dépenses — {edit?.titre}</DialogTitle>
          </DialogHeader>
          {edit && (
            <div className="grid grid-cols-2 gap-4">
              {[
                ["Coût réel HT", "cout_reel_ht"],
                ["CA facturé HT", "ca_facture_ht"],
                ["CA encaissé HT", "ca_encaisse_ht"],
                ["Heures passées", "heures_passees"],
                ["Avancement %", "progression_pct"],
                ["Budget HT", "budget_ht"],
              ].map(([label, key]) => (
                <div key={key} className="space-y-1.5">
                  <Label className="text-slate-400 text-xs">{label}</Label>
                  <Input
                    type="number"
                    data-testid={`chantier-input-${key}`}
                    value={edit[key]}
                    onChange={(e) => setEdit({ ...edit, [key]: e.target.value })}
                    className="bg-[#0B0F17] border-[#1E293B] font-mono-metric"
                  />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEdit(null)} className="text-slate-400">Annuler</Button>
            <Button data-testid="chantier-save-button" onClick={saveEdit} className="bg-amber-500 hover:bg-amber-600 text-[#0B0F17] font-semibold">
              Recalculer la rentabilité
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
